import { useEffect, useMemo, useState } from 'react';

import { Quote } from '@meterup/connect-api';

// Utils
import { onlyUnique } from '../utils/Array.utils';

// A hooks to keep quotes logic / filtering
export default function useMonthlyFeeFilter(quotes: Quote[]) {
  const quotesByContractLengthMonths = useMemo(
    () =>
      [...quotes]
        .sort((a, b) => (a.monthlyFeeCents ?? 0) - (b.monthlyFeeCents ?? 0))
        .reduce<Record<string, Quote[]>>((acc, q) => {
          const key = q?.contractLengthMonths ?? 'unknown';
          (acc[key] ??= []).push(q);
          return acc;
        }, {}),
    [quotes],
  );

  // Contract Length - All
  const allContractLengthMonths = useMemo(
    () =>
      quotes
        .map((quote: Quote) => quote.contractLengthMonths)
        .filter(onlyUnique)
        .sort((a, b) => (a < b ? -1 : 1)),
    [quotes],
  );

  // Contract Length - Currently selected (default: all)
  const [currentContractLengthMonths, setCurrentContractLengthMonths] =
    useState(allContractLengthMonths);

  // Contract Length - setstate
  useEffect(() => {
    setCurrentContractLengthMonths(allContractLengthMonths);
  }, [allContractLengthMonths]);

  // Contract Length - User event change
  const onContractLengthMonthsChange = (contractLengthMonth: number, checked: boolean) => {
    let newContractLengthMonths = currentContractLengthMonths;
    if (checked) {
      newContractLengthMonths.push(contractLengthMonth);
    } else {
      newContractLengthMonths = newContractLengthMonths.filter(
        (item) => item !== contractLengthMonth,
      );
    }
    setCurrentContractLengthMonths(newContractLengthMonths.filter(onlyUnique));
  };

  const resetContractLengthMonthsFilter = () => {
    setCurrentContractLengthMonths(allContractLengthMonths);
  };

  return {
    quotesByContractLengthMonths,
    allContractLengthMonths,
    currentContractLengthMonths,
    onContractLengthMonthsChange,
    resetContractLengthMonthsFilter,
  };
}
